/**
 * 掲示板 投稿テンプレート (board_templates) の hooks。
 * 仕様書: docs/02_work/done/plan_board_feature.md（テンプレート機能 2026-07-25 追加）
 *
 * - 標準テンプレートはコード定数 (pages/board/boardTemplates.ts)。
 *   ここで扱うのはユーザーが追加したカスタムテンプレートのみ。
 * - 読込は Firestore 直接 (read は rules の isInternal で保護)。
 * - 作成・削除はクライアント直書き (safeWrite、rules の作成者バインド)。
 */
import type { BoardPostType, BoardTemplate, BoardUrgency } from '@goenhub/shared';
import { BOARD_COLLECTIONS } from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';

import { useAuth } from '@/hooks/useAuth';
import { db } from '@/lib/firebase';
import { safeAddDoc, safeDeleteDoc } from '@/lib/safeWrite';

export interface BoardTemplateRecord extends BoardTemplate {
  id: string;
}

export function useBoardTemplates() {
  return useQuery({
    queryKey: ['board_templates'],
    queryFn: async (): Promise<BoardTemplateRecord[]> => {
      const q = query(
        collection(db, BOARD_COLLECTIONS.templates),
        orderBy('createdAt', 'desc'),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as BoardTemplate) }));
    },
  });
}

export interface CreateBoardTemplateInput {
  name: string;
  subject: string;
  bodyHtml: string;
  type?: BoardPostType;
  urgency?: BoardUrgency;
}

/** カスタムテンプレートの作成（クライアント直書き）。 */
export function useCreateBoardTemplate() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: CreateBoardTemplateInput): Promise<{ id: string }> => {
      if (!user) throw new Error('未認証です');
      const ref = await safeAddDoc(collection(db, BOARD_COLLECTIONS.templates), {
        name: input.name,
        subject: input.subject,
        bodyHtml: input.bodyHtml,
        // 省略時はフィールド自体を書かない（rules の hasOnly と整合、undefined は Firestore 不可）
        ...(input.type ? { type: input.type } : {}),
        ...(input.urgency ? { urgency: input.urgency } : {}),
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { id: ref.id };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['board_templates'] }),
  });
}

/** カスタムテンプレートの削除（作成者 or owner。rules 側でも検証）。 */
export function useDeleteBoardTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      await safeDeleteDoc(doc(db, BOARD_COLLECTIONS.templates, templateId));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['board_templates'] }),
  });
}
